/**
 * どの業種にどのメニューを出すか。ここが唯一の出どころ。
 *
 * なぜ要るか:
 *   出し分けが1つも無く、全業種に31項目すべてが並んでいた。
 *   実測すると、居酒屋のオーナーには31項目のうち8項目が
 *   一生使わないものだった（ボトルキープ・日払い・バック承認など）。
 *   コンカフェには6項目。
 *
 *   使わない項目が並ぶと、ITに詳しくない人ほど
 *   「これも設定しないと使えないのか」と手が止まる。
 *   減らすのではなく、その店に関係のあるものだけを出す。
 *
 * 消すのではなく隠すだけ:
 *   画面と機能は残す。業態が変わったとき、設定から出せるようにする。
 */

export type ShopCategory = 'nightlife' | 'concafe' | 'restaurant';

/** 業種が分からないときは、いちばん多く出す（隠して困るより出して選ばせる）。 */
export const DEFAULT_CATEGORY: ShopCategory = 'nightlife';

/**
 * 店の情報から業種を決める。
 *
 * categoryId が本命。古い店は businessTypes.categories にしか入っていないので
 * そちらも見る。どちらも無ければ既定に倒す。
 */
export function resolveCategory(shop: unknown): ShopCategory {
  const s = (shop ?? {}) as Record<string, any>;
  const raw = s.categoryId
    ?? (Array.isArray(s.businessTypes?.categories) ? s.businessTypes.categories[0] : undefined);
  return raw === 'concafe' || raw === 'restaurant' || raw === 'nightlife' ? raw : DEFAULT_CATEGORY;
}

/**
 * その業種で使わないメニュー。
 *
 * 「使う」ではなく「使わない」を並べるのは、
 * メニューを足したときに、書き忘れても消えないようにするため。
 * 書き忘れで出るのは直せるが、書き忘れで消えると気づけない。
 */
const HIDDEN: Record<ShopCategory, string[]> = {
  // 一般飲食: 指名・バック・ツケ・キープの文化が無い
  restaurant: [
    'bottleKeeps',       // ボトルキープ
    'receivables',       // 未収金（ツケ）
    'allowanceTypes',    // 手当マスタ
    'backApproval',      // バック承認
    'payouts',           // 日払い
    'blacklist',         // ブラックリスト
    'timeChargeSettings',// 時間制の席料
  ],
  // コンカフェ: ボトルもツケも日払いも基本的に無い。
  // 飲み放題より「時間制の席料」が主。
  concafe: [
    'bottleKeeps',
    'receivables',
    'payouts',
    'blacklist',
    'planManagement',    // 飲み放題プラン
    'mobileOrderSettings',
  ],
  // 夜職: 客のQR注文は使わない（キャストの紐づけが付かないため）
  nightlife: [
    'mobileOrderSettings',
  ],
};

/** その店で出すメニューか */
export function isMenuVisible(key: string, category: ShopCategory): boolean {
  return !HIDDEN[category].includes(key);
}

/** 隠している項目の数（設定画面で「他にもある」と伝えるため） */
export function hiddenCount(category: ShopCategory): number {
  return HIDDEN[category].length;
}

export const HIDDEN_BY_CATEGORY = HIDDEN;
